import { InfoResponse } from "@/@types/growthos-api/schema/openapi";

export type NodeRefs = string[];
const NODE_LAYERS = ["customer", "brand", "category", "product"];
// Allow the 4th layer to be either "product_line" or "category" (mutually exclusive).
// We extend the NodeLayer union with "category" while keeping the original NODE_LAYERS
// array intact so that the overall hierarchy depth and ordering remain unchanged.
export type NodeLayer = (typeof NODE_LAYERS)[number];
export type NodeLayers = NodeLayer[];
export type FastFilterType = {
  [key in NodeLayer]?: Record<string, boolean>;
};
export type NodeProps = {
  index: number;
  ref: NodeRefs;
  name: string;
  layer: NodeLayers;
  parent: null | number;
  children: number[];
  extras: Record<string, any>;
};
export type AllNodePerLayer = {
  [key in NodeLayer]?: Node[];
};
export type AllCollectionPerLayer = {
  [key in NodeLayer]?: Record<number, Node[]>;
};

const isNodeLayer = (a: unknown): a is NodeLayer => {
  return NODE_LAYERS.indexOf(a as NodeLayer) != -1;
};

export class Node {
  index: number;
  ref: NodeRefs;
  name: string;
  layer: NodeLayers;
  parent: null | number;
  children: number[];
  extras: Record<string, any>;

  constructor({ index, ref, name, layer, parent = null, children = [], extras = {} }: NodeProps) {
    if (layer.every((layerName) => isNodeLayer(layerName)) === false) {
      throw TypeError("Invalid layer!");
    }
    this.index = index;
    this.ref = ref;
    this.name = name;
    this.layer = layer;
    this.parent = parent;
    this.children = children;
    this.extras = { ...extras };
  }

  static fromJSON(jsonData: NodeProps): Node {
    return new Node({ ...jsonData });
  }

  equals(otherRef: NodeRefs): boolean {
    return (
      otherRef.length === this.ref.length &&
      otherRef.every((value, index) => value === this.ref[index])
    );
  }

  get layerName(): NodeLayer {
    const layerName = this.layer[this.layer.length - 1];
    if (layerName !== undefined) return layerName;
    throw TypeError("Layer name does not exist!");
  }
}

export class Tree {
  node: Node[];

  constructor({ node }: { node: Node[] }) {
    this.node = node;
  }

  static fromJSON(jsonData: { node: NodeProps[] }): Tree {
    return new Tree({
      node: (jsonData.node || []).map((data) => Node.fromJSON(data)),
    });
  }

  // write getNodeTree method here
  getNodeTree(): Tree {
    return this;
  }

  // write getNodeTree method here
  getNodeTreeExtras(): Record<string, any>[] {
    return this.node.map((node) => node.extras);
  }

  getNodeByRef(metaRef: NodeRefs): Node | undefined {
    for (const node of this.node) {
      if (node.equals(metaRef)) return node;
    }

    return;
  }

  getNodeById(nodeId: number): Node {
    const node = this.node[nodeId];
    if (node !== undefined) return node;
    throw TypeError("Node does not exist!");
  }

  addNameMapping(
    mappingData: InfoResponse["configData"]["mapping"]["meta"],
    nodeMetaLayer: InfoResponse["configData"]["mapping"]["nodeMetaLayer"]
  ) {
    this.node.forEach((node) => {
      const nodeMetaLayerMapping =
        nodeMetaLayer && nodeMetaLayer[node.layerName as keyof typeof nodeMetaLayer];
      const mappingConfig = nodeMetaLayerMapping || mappingData;
      node.extras.mappedName = mappingConfig[node.name] || node.name;
      node.extras.mappedRef = node.ref.map((ref) => mappingConfig[ref] || ref);
    });
  }

  isFilteredOut(fastFilter: FastFilterType, node: Node): boolean {
    // hierarchy filter check
    return node.layer.some(
      (layer, index) => fastFilter[layer]?.[node.extras.mappedRef[index]] === false
    );
  }

  getAllNodePerLayer(fastFilter: FastFilterType = {}): AllNodePerLayer {
    const data: AllNodePerLayer = {};
    let layers: NodeLayers = [];
    // loop through all node
    this.node.forEach((node) => {
      // check node is not default (skipped node)
      if (node.name === "default") return;
      // fast filter
      if (this.isFilteredOut(fastFilter, node)) return;
      // get layer name
      const layer: NodeLayer = node.layerName;
      // get layers
      if (node.layer.length > layers.length) layers = node.layer;
      // add node to data
      const dataLayer = data[layer];
      if (dataLayer !== undefined) dataLayer.push(node);
      else data[layer] = [node];
    });
    return data;
  }

  getAllCollectionPerLayer(
    fastFilter: FastFilterType = {},
    allowSingleNode = false
  ): AllCollectionPerLayer {
    // generate collection using child node
    const data: AllCollectionPerLayer = {};
    // loop through all node
    this.node.forEach((node) => {
      // fast filter on current node
      if (this.isFilteredOut(fastFilter, node)) return;
      const collection: Node[] = [];
      // loop through child node
      node.children.forEach((nodeId) => {
        // get child node
        const childNode: Node = this.getNodeById(nodeId);
        // check node is not default (skipped node)
        if (childNode.name === "default") return;
        // fast filter on child node
        if (this.isFilteredOut(fastFilter, childNode)) return;
        // add child node
        collection.push(childNode);
      });

      // add data if collection has more than nodesInCollectionLimit nodes and no child node missing\
      const nodesInCollectionLimit = allowSingleNode ? 0 : 1;

      if (
        collection.length > nodesInCollectionLimit &&
        collection.length ===
          node.children.filter((childNodeId) => this.getNodeById(childNodeId).name !== "default")
            .length
      ) {
        // get child node
        const childNode = collection[0];
        if (childNode === undefined) throw TypeError("Invalid collection!");
        // get parent node with name != default
        // node is already a parent in this case
        const parentNode: Node = this.getNonDefaultNode(node);
        // add to data
        const dataLayer = data[parentNode.layerName];
        if (dataLayer !== undefined) dataLayer[parentNode.index] = collection;
        else data[parentNode.layerName] = { [parentNode.index]: collection };
      }
    });
    return data;
  }

  getLeafNode(): Node[] {
    return this.node.filter((node) => node.children.length === 0);
  }

  getNonDefaultLeafNode(): Node[] {
    const nonDefaultLeafNode: Node[] = [];
    this.getLeafNode().forEach((node) => {
      const nonDefaultNode = this.getNonDefaultNode(node);
      if (nonDefaultNode.index === 0) return;
      if (nonDefaultNode.children.length > 1) return;
      nonDefaultLeafNode.push(nonDefaultNode);
    });
    if (nonDefaultLeafNode.length === 0) nonDefaultLeafNode.push(this.getNodeById(0));
    return nonDefaultLeafNode;
  }

  getNonDefaultNode(currentNode: Node): Node {
    while (currentNode.name === "default") {
      if (currentNode.parent === null) throw TypeError("Invalid node!");
      currentNode = this.getNodeById(currentNode.parent);
    }
    return currentNode;
  }

  getNonDefaultParentNode(currentNode: Node): Node | null {
    if (currentNode.parent === null) return null;
    return this.getNonDefaultNode(this.getNodeById(currentNode.parent));
  }
}
